'use client'

import * as React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../Drawer'
import { cn } from '../../../lib/utils'
import Icon, { type iconTypes } from '../Icon'
import { useIsMobile } from '../Threads/hooks/useMediaQuery'
import {
  ViewType,
  LayoutOptionsPanel,
  type LayoutOptionsPanelProps,
} from '../LayoutOptionsPanel'
import { QueryBar, type QueryBarProps } from '../QueryBar'
import { BaseQueryPanel, type BaseQueryPanelProps } from '../BaseQueryPanel'
import { Select } from '../Select'

export type PageDrawerModelOption = {
  value: string
  label: string
}

export interface PageDrawerProps {
  page: {
    name: string
    icon?: iconTypes
  }
  count?: number
  modelName?: string
  modelId?: string
  modelOptions?: PageDrawerModelOption[]
  onLoadModelOptions?: () => Promise<PageDrawerModelOption[]>
  onPageModelChange?: (
    modelId: string,
    modelLabel?: string,
  ) => void | Promise<void>
  /** When true, model select is disabled (default list pages). */
  isDefaultPage?: boolean
  currentView: ViewType
  onViewChange?: (view: ViewType) => void
  // Layout options can be provided either as pre-rendered content or as props
  layoutContent?: React.ReactNode
  layoutProps?: Omit<LayoutOptionsPanelProps, 'view' | 'onViewChange'>
  importContent?: React.ReactNode
  onImportClick?: () => void
  onPageNameChange?: (name: string) => void | Promise<void>
  queryBar?: QueryBarProps
  baseQueryPanel?: BaseQueryPanelProps
  onShareWithTeam?: () => void | Promise<void>
  showShareWithTeam?: boolean
  trigger: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

type ActivePanel = 'page' | 'layout' | 'import'
type MobileView = 'main' | 'page' | 'layout' | 'import'

const DESKTOP_MENU_HEIGHT = 400
const DESKTOP_DRAWER_WIDTH = 540
const DESKTOP_LEFT_COLUMN_WIDTH = 200
const DESKTOP_RIGHT_COLUMN_WIDTH = 320
const menuItemRadius = 'rounded-xl'
const menuInsetX = 'mx-2'
const menuItemPadding = 'px-3 py-2.5'

const viewIcons: Record<ViewType, iconTypes> = {
  spreadsheet: 'TableProperties',
  kanban: 'SquareKanban',
  calendar: 'Calendar',
}

export function PageDrawer({
  page,
  count,
  modelName,
  modelId,
  modelOptions,
  onLoadModelOptions,
  onPageModelChange,
  isDefaultPage = false,
  currentView,
  onViewChange,
  layoutContent,
  layoutProps,
  importContent,
  onImportClick,
  onPageNameChange,
  queryBar,
  baseQueryPanel,
  onShareWithTeam,
  showShareWithTeam,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: PageDrawerProps) {
  const isMobile = useIsMobile()
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [activePanel, setActivePanel] = React.useState<ActivePanel>('layout')
  const [mobileView, setMobileView] = React.useState<MobileView>('main')

  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setInternalOpen(open)
    }
    controlledOnOpenChange?.(open)
    if (open) {
      setActivePanel('layout')
    } else {
      setActivePanel('layout')
      setMobileView('main')
    }
  }

  const [loadedModelOptions, setLoadedModelOptions] = React.useState<
    PageDrawerModelOption[] | undefined
  >(undefined)

  const resolvedModelOptions = React.useMemo(() => {
    if (modelOptions?.length) {
      return modelOptions
    }
    return loadedModelOptions
  }, [loadedModelOptions, modelOptions])

  React.useEffect(() => {
    if (!isOpen) return
    if (modelOptions?.length) return
    if (!onLoadModelOptions) return
    if (loadedModelOptions?.length) return

    let cancelled = false
    void onLoadModelOptions()
      .then((options) => {
        if (!cancelled && options.length) {
          setLoadedModelOptions(options)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [
    isOpen,
    loadedModelOptions?.length,
    modelOptions?.length,
    onLoadModelOptions,
  ])

  // Render layout content: prefer explicit content, fallback to LayoutOptionsPanel
  const renderLayoutContent = () => {
    if (layoutContent) {
      return layoutContent
    }
    if (layoutProps) {
      return (
        <LayoutOptionsPanel
          view={currentView}
          onViewChange={onViewChange || (() => {})}
          {...layoutProps}
        />
      )
    }
    return null
  }

  const handleMobileBack = () => {
    setMobileView('main')
  }

  const closeMenu = () => handleOpenChange(false)

  const drawerQueryBar = queryBar ? (
    <div
      className="border-b border-border/60 px-3 py-2.5 shrink-0"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <QueryBar {...queryBar} showLayoutControls={false} autoFocus={isOpen} />
    </div>
  ) : null

  const mobileContent = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden w-full">
      {drawerQueryBar}
      {mobileView === 'main' && (
        <MobileMainView
          page={page}
          currentView={currentView}
          onPageClick={() => setMobileView('page')}
          onLayoutClick={() => setMobileView('layout')}
          onImportClick={() => {
            if (importContent) {
              setMobileView('import')
            } else if (onImportClick) {
              onImportClick()
              closeMenu()
            }
          }}
          hasImportContent={!!importContent || !!onImportClick}
        />
      )}
      {mobileView === 'page' && (
        <MobileNestedView title="Page" onBack={handleMobileBack}>
          <PagePanel
            page={page}
            modelName={modelName}
            modelId={modelId}
            modelOptions={resolvedModelOptions}
            onPageModelChange={isDefaultPage ? undefined : onPageModelChange}
            isDefaultPage={isDefaultPage}
            onPageNameChange={onPageNameChange}
            baseQueryPanel={baseQueryPanel}
            onShareWithTeam={onShareWithTeam}
            showShareWithTeam={showShareWithTeam}
          />
        </MobileNestedView>
      )}
      {mobileView === 'layout' && (
        <MobileNestedView title="Layout" onBack={handleMobileBack}>
          <div className="px-2">{renderLayoutContent()}</div>
        </MobileNestedView>
      )}
      {mobileView === 'import' && importContent && (
        <MobileNestedView title="Import" onBack={handleMobileBack}>
          <div className="px-2">{importContent}</div>
        </MobileNestedView>
      )}
    </div>
  )

  const desktopContent = (
    <div className="flex flex-col" style={{ width: DESKTOP_DRAWER_WIDTH }}>
      {drawerQueryBar}
      <div className="flex" style={{ height: DESKTOP_MENU_HEIGHT }}>
        <LeftColumn
          page={page}
          currentView={currentView}
          activePanel={activePanel}
          onHoverPage={() => setActivePanel('page')}
          onHoverLayout={() => setActivePanel('layout')}
          onHoverImport={() => setActivePanel('import')}
          hasImportContent={!!importContent || !!onImportClick}
        />
        <RightColumn
          activePanel={activePanel}
          page={page}
          modelName={modelName}
          modelId={modelId}
          modelOptions={resolvedModelOptions}
          onPageModelChange={isDefaultPage ? undefined : onPageModelChange}
          isDefaultPage={isDefaultPage}
          layoutContent={renderLayoutContent()}
          importContent={importContent}
          onPageNameChange={onPageNameChange}
          baseQueryPanel={baseQueryPanel}
          onShareWithTeam={onShareWithTeam}
          showShareWithTeam={showShareWithTeam}
        />
      </div>
    </div>
  )

  const mobileDrawerTitle =
    mobileView === 'page'
      ? 'Page'
      : mobileView === 'layout'
        ? 'Layout'
        : mobileView === 'import'
          ? 'Import'
          : page.name

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[85vh] overflow-hidden p-0">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{mobileDrawerTitle}</DrawerTitle>
          </DrawerHeader>
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-6"
            style={{ maxHeight: 'calc(85vh - 2rem)' }}
          >
            {mobileContent}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        className={cn(
          'p-0 w-auto rounded-2xl overflow-hidden shadow-lg',
          queryBar && 'min-w-[540px]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2',
        )}
      >
        {desktopContent}
      </PopoverContent>
    </Popover>
  )
}

interface LeftColumnProps {
  page: PageDrawerProps['page']
  currentView: ViewType
  activePanel: ActivePanel
  onHoverPage: () => void
  onHoverLayout: () => void
  onHoverImport: () => void
  hasImportContent: boolean
}

function LeftColumn({
  page,
  currentView,
  activePanel,
  onHoverPage,
  onHoverLayout,
  onHoverImport,
  hasImportContent,
}: LeftColumnProps) {
  return (
    <div
      className="flex shrink-0 flex-col py-3 px-1 border-r border-border/60 h-full"
      style={{ width: DESKTOP_LEFT_COLUMN_WIDTH }}
    >
      {/* Page Row */}
      <button
        type="button"
        onMouseEnter={onHoverPage}
        onClick={onHoverPage}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'transition-colors duration-200 text-left',
          'hover:bg-accent/50',
          activePanel === 'page' && 'bg-accent',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon
            name={page.icon || 'FileText'}
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
          <span className="text-sm text-foreground truncate">Page</span>
        </span>
        <Icon
          name="ChevronRight"
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            activePanel === 'page' && 'translate-x-0.5',
          )}
        />
      </button>

      {/* Layout Row */}
      <button
        type="button"
        onMouseEnter={onHoverLayout}
        onClick={onHoverLayout}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'transition-colors duration-200 text-left',
          'hover:bg-accent/50',
          activePanel === 'layout' && 'bg-accent',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon
            name={viewIcons[currentView]}
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
          <span className="text-sm text-foreground">Layout</span>
        </span>
        <Icon
          name="ChevronRight"
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            activePanel === 'layout' && 'translate-x-0.5',
          )}
        />
      </button>

      {/* Import Row */}
      {hasImportContent && (
        <>
          <div className="my-2 mx-2 h-px bg-border" />
          <button
            type="button"
            onMouseEnter={onHoverImport}
            onClick={onHoverImport}
            className={cn(
              'w-full flex items-center justify-between',
              menuItemPadding,
              menuInsetX,
              menuItemRadius,
              'transition-colors duration-200 text-left',
              'hover:bg-accent/50',
              activePanel === 'import' && 'bg-accent',
              'cursor-pointer',
            )}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Icon
                name="Upload"
                className="h-4 w-4 text-muted-foreground shrink-0"
              />
              <span className="text-sm text-foreground">Import</span>
            </span>
            <Icon
              name="ChevronRight"
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                activePanel === 'import' && 'translate-x-0.5',
              )}
            />
          </button>
        </>
      )}
    </div>
  )
}

interface RightColumnProps {
  activePanel: ActivePanel
  page: PageDrawerProps['page']
  modelName?: string
  modelId?: string
  modelOptions?: PageDrawerModelOption[]
  onPageModelChange?: PageDrawerProps['onPageModelChange']
  isDefaultPage?: boolean
  layoutContent?: React.ReactNode
  importContent?: React.ReactNode
  onPageNameChange?: PageDrawerProps['onPageNameChange']
  baseQueryPanel?: BaseQueryPanelProps
  onShareWithTeam?: PageDrawerProps['onShareWithTeam']
  showShareWithTeam?: boolean
}

function RightColumn({
  activePanel,
  page,
  modelName,
  modelId,
  modelOptions,
  onPageModelChange,
  isDefaultPage,
  layoutContent,
  importContent,
  onPageNameChange,
  baseQueryPanel,
  onShareWithTeam,
  showShareWithTeam,
}: RightColumnProps) {
  return (
    <div
      className="flex shrink-0 flex-col py-3 px-2 h-full overflow-hidden"
      style={{ width: DESKTOP_RIGHT_COLUMN_WIDTH }}
    >
      <div
        key={activePanel}
        className="flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden animate-in fade-in-0 duration-150"
      >
        {activePanel === 'page' && (
          <PagePanel
            page={page}
            modelName={modelName}
            modelId={modelId}
            modelOptions={modelOptions}
            onPageModelChange={onPageModelChange}
            isDefaultPage={isDefaultPage}
            onPageNameChange={onPageNameChange}
            baseQueryPanel={baseQueryPanel}
            onShareWithTeam={onShareWithTeam}
            showShareWithTeam={showShareWithTeam}
          />
        )}
        {activePanel === 'layout' && layoutContent}
        {activePanel === 'import' && importContent}
      </div>
    </div>
  )
}

interface PagePanelProps {
  page: PageDrawerProps['page']
  modelName?: string
  modelId?: string
  modelOptions?: PageDrawerModelOption[]
  onPageModelChange?: PageDrawerProps['onPageModelChange']
  isDefaultPage?: boolean
  onPageNameChange?: PageDrawerProps['onPageNameChange']
  baseQueryPanel?: BaseQueryPanelProps
  onShareWithTeam?: PageDrawerProps['onShareWithTeam']
  showShareWithTeam?: boolean
}

const pagePanelInputClassName = cn(
  'w-full rounded-md border border-border bg-background px-2.5 py-1.5',
  'text-sm text-foreground placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-60',
)

function PagePanel({
  page,
  modelName,
  modelId,
  modelOptions,
  onPageModelChange,
  isDefaultPage = false,
  onPageNameChange,
  baseQueryPanel,
  onShareWithTeam,
  showShareWithTeam,
}: PagePanelProps) {
  const [draft, setDraft] = React.useState(page.name)
  const [isSaving, setIsSaving] = React.useState(false)
  const [selectedModelId, setSelectedModelId] = React.useState(modelId ?? '')

  React.useEffect(() => {
    setDraft(page.name)
  }, [page.name])

  React.useEffect(() => {
    setSelectedModelId(modelId ?? '')
  }, [modelId])

  const showModelField =
    Boolean(onPageModelChange) ||
    Boolean(modelId) ||
    Boolean(modelName) ||
    Boolean(modelOptions?.length) ||
    isDefaultPage

  const selectItems = React.useMemo(() => {
    if (modelOptions?.length) {
      return modelOptions.map((option) => ({
        value: option.value,
        label: option.label,
      }))
    }
    if (modelId && modelName) {
      return [{ value: modelId, label: modelName }]
    }
    return []
  }, [modelId, modelName, modelOptions])

  const handleModelChange = React.useCallback(
    async (nextModelId: string) => {
      if (
        isDefaultPage ||
        !onPageModelChange ||
        !nextModelId ||
        nextModelId === modelId
      ) {
        return
      }

      const nextLabel =
        selectItems.find((item) => item.value === nextModelId)?.label ??
        'the selected model'

      // Keep the select on the current model until the page prop updates after confirm.
      setSelectedModelId(modelId ?? '')
      await onPageModelChange(nextModelId, nextLabel)
    },
    [isDefaultPage, modelId, onPageModelChange, selectItems],
  )

  const commit = React.useCallback(async () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      setDraft(page.name)
      return
    }
    if (trimmed === page.name || !onPageNameChange) {
      return
    }

    setIsSaving(true)
    try {
      await onPageNameChange(trimmed)
    } finally {
      setIsSaving(false)
    }
  }, [draft, onPageNameChange, page.name])

  return (
    <div className="flex flex-col gap-2 px-1 py-1">
      <label
        htmlFor="page-drawer-name"
        className="text-xs font-medium text-muted-foreground"
      >
        Name
      </label>
      <input
        id="page-drawer-name"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          void commit()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            setDraft(page.name)
            event.currentTarget.blur()
          }
        }}
        disabled={!onPageNameChange || isSaving}
        className={pagePanelInputClassName}
        placeholder="Page name"
      />

      {showModelField && (
        <>
          <label
            htmlFor="page-drawer-model"
            className="text-xs font-medium text-muted-foreground mt-1"
          >
            Model
          </label>
          {onPageModelChange && !isDefaultPage && selectItems.length > 0 ? (
            <Select
              id="page-drawer-model"
              value={selectedModelId || null}
              items={selectItems}
              placeholder="Select model"
              isDisabled={isSaving}
              triggerClassName={pagePanelInputClassName}
              handleChange={(value) => {
                void handleModelChange(value)
              }}
            />
          ) : (
            <input
              id="page-drawer-model"
              value={modelName ?? ''}
              disabled
              readOnly
              title={
                isDefaultPage
                  ? 'Default list pages are locked to their model'
                  : undefined
              }
              className={cn(pagePanelInputClassName, 'bg-muted/40')}
            />
          )}
        </>
      )}

      {baseQueryPanel && (
        <BaseQueryPanel {...baseQueryPanel} className="mt-3" />
      )}

      {showShareWithTeam && onShareWithTeam && (
        <button
          type="button"
          onClick={() => {
            void onShareWithTeam()
          }}
          className={cn(
            'mt-3 w-full rounded-md border border-border px-3 py-2 text-sm',
            'hover:bg-muted/50 transition-colors',
          )}
        >
          Share with team
        </button>
      )}
    </div>
  )
}

interface MobileMainViewProps {
  page: PageDrawerProps['page']
  currentView: ViewType
  onPageClick: () => void
  onLayoutClick: () => void
  onImportClick: () => void
  hasImportContent: boolean
}

function MobileMainView({
  page,
  currentView,
  onPageClick,
  onLayoutClick,
  onImportClick,
  hasImportContent,
}: MobileMainViewProps) {
  return (
    <div className="w-full py-2">
      {/* Page */}
      <button
        onClick={onPageClick}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon
            name={page.icon || 'FileText'}
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
          <span className="text-sm text-foreground">Page</span>
        </span>
        <Icon name="ChevronRight" className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Layout */}
      <button
        onClick={onLayoutClick}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon
            name={viewIcons[currentView]}
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
          <span className="text-sm text-foreground">Layout</span>
        </span>
        <Icon name="ChevronRight" className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Import */}
      {hasImportContent && (
        <>
          <div className="my-2 mx-2 h-px bg-border" />
          <button
            onClick={onImportClick}
            className={cn(
              'w-full flex items-center justify-between',
              menuItemPadding,
              menuInsetX,
              menuItemRadius,
              'hover:bg-accent/50 transition-colors duration-200',
              'text-left cursor-pointer',
            )}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Icon
                name="Upload"
                className="h-4 w-4 text-muted-foreground shrink-0"
              />
              <span className="text-sm text-foreground">Import</span>
            </span>
            <Icon
              name="ChevronRight"
              className="h-4 w-4 text-muted-foreground"
            />
          </button>
        </>
      )}
    </div>
  )
}

interface MobileNestedViewProps {
  title: string
  onBack: () => void
  children: React.ReactNode
}

function MobileNestedView({ title, onBack, children }: MobileNestedViewProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden w-full">
      <button
        onClick={onBack}
        className={cn(
          'w-full flex shrink-0 items-center gap-2 px-4 py-4 border-b border-border',
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
      >
        <Icon name="ChevronLeft" className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{title}</span>
      </button>
      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto py-2 px-1 pt-1">
        {children}
      </div>
    </div>
  )
}

export type { ViewType }
